import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplets, BadgeCheck, Truck, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { LogoMark } from "../components/Logo";

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(["admin", "operator"].includes(user.role) ? "/admin" : "/cont", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bine ai revenit!");
      } else {
        if (!form.name.trim()) {
          setError("Completează numele tău");
          setLoading(false);
          return;
        }
        await register(form.name, form.email, form.password);
        toast.success("Cont creat cu succes!");
      }
    } catch (err) {
      setError(apiError(err, "Autentificarea a eșuat. Încearcă din nou."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mx-auto grid max-w-6xl gap-0 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="relative hidden overflow-hidden rounded-l-[2rem] bg-navy p-12 lg:flex lg:flex-col lg:justify-between"
      >
        <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-lg bg-sky2/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-lg bg-sky-500/20 blur-3xl" />
        <div className="relative flex items-center gap-2.5">
          <LogoMark className="h-10 w-10" />
          <span className="font-display text-xl font-bold text-white">
            Detergent<span className="text-sky-light">Zilnic</span>
          </span>
        </div>
        <div className="relative">
          <h2 className="font-display text-3xl font-extrabold leading-tight text-white">
            Prospețime supremă,<br />la un cont distanță.
          </h2>
          <ul className="mt-8 flex flex-col gap-4 text-sm text-line">
            <li className="flex items-center gap-3"><BadgeCheck className="h-5 w-5 text-sky-light" /> Istoric comenzi și urmărire status</li>
            <li className="flex items-center gap-3"><Truck className="h-5 w-5 text-sky-light" /> Checkout mai rapid cu datele salvate</li>
            <li className="flex items-center gap-3"><Banknote className="h-5 w-5 text-sky-light" /> Plată ramburs, fără riscuri</li>
          </ul>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-muted2">
          <Droplets className="h-4 w-4 text-sky-light" />
          detergentzilnic.ro — curățenie profesională, zilnic
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="rounded-lg border border-line bg-white p-8 shadow-xl shadow-navy/5 sm:p-12 lg:rounded-l-none"
      >
        <div className="flex rounded-lg bg-skytint p-1" data-testid="auth-mode-toggle">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${mode === m ? "bg-white text-navy shadow" : "text-muted2"}`}
              data-testid={`auth-mode-${m}`}
            >
              {m === "login" ? "Autentificare" : "Cont nou"}
            </button>
          ))}
        </div>

        <h1 className="mt-8 font-display text-2xl font-extrabold text-navy">
          {mode === "login" ? "Bine ai revenit!" : "Creează-ți contul"}
        </h1>
        <p className="mt-1.5 text-sm text-muted2">
          {mode === "login"
            ? "Autentifică-te pentru a-ți vedea comenzile."
            : "Durează mai puțin de un minut."}
        </p>

        <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted2">Nume complet</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg border border-line px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                placeholder="Numele tău"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted2">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg border border-line px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
              placeholder="email@exemplu.ro"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted2">Parolă</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-lg border border-line px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
              placeholder="Minim 6 caractere"
              data-testid="auth-password-input"
            />
          </div>
          {error && (
            <p className="rounded-lg bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600" data-testid="auth-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="mt-2 w-full rounded-lg bg-navy py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-navy hover:shadow-lg hover:shadow-navy/30 disabled:opacity-60"
            data-testid="auth-submit-button"
          >
            {loading ? "Se procesează..." : mode === "login" ? "Autentifică-mă" : "Creează contul"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
